import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import type {Branding} from '../api/types';
import {useGuestSession} from '../session';
import {AccentStyles} from '../theme';

/**
 * The merchant's accent colour and logo, read once per session and applied.
 *
 * ## Why this exists, and why now
 *
 * Until 2026-08-21 the guest surface was light-only with no venue logo, on
 * purpose. The owner reversed that: a merchant who has set a brand should see it,
 * so `GET /v1/branding` is read and the accent + logo applied. It is mounted at
 * the root, beside `ServiceStatusProvider`, because it needs the credential and
 * because two very different consumers read it — the theme (the accent) and the
 * menu header (the logo) — so one read published to everybody beats two.
 *
 * ## A shop with no branding is the ordinary case, never an error
 *
 * The default context is a dead, unconfigured state rather than `undefined`, and
 * `useI18n`'s "throw outside the provider" discipline is deliberately not copied:
 * a screen in a test harness with no provider, a `configured: false` response, and
 * a server that declines the route (`client.getBranding` turns 501/401/403/404
 * into `null`) all mean the same thing — keep the default look. An accent that is
 * not a `#rrggbb` colour is dropped by `AccentStyles`; a missing logo leaves the
 * name-based identity in place. Nothing here can break the default design.
 */

export interface BrandingValue {
  /** True only when the server said so AND at least one brand field is usable. */
  configured: boolean;
  /** A `#rrggbb` accent, or null. Validated downstream before it reaches CSS. */
  accentColor: string | null;
  /** An absolute logo URL, or null. */
  logoUrl: string | null;
}

const UNBRANDED: BrandingValue = {
  configured: false,
  accentColor: null,
  logoUrl: null,
};

const BrandingContext = createContext<BrandingValue>(UNBRANDED);

/** A blank or whitespace string counts as absent — a saved-but-empty field. */
function orNull(value: string | null | undefined): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

function toValue(branding: Branding | null): BrandingValue {
  if (!branding || !branding.configured) return UNBRANDED;
  const accentColor = orNull(branding.accent_color);
  const logoUrl = orNull(branding.logo_url);
  // `configured: true` with nothing usable behaves as unbranded — there is
  // nothing to apply, and callers reading `configured` should not light up.
  if (!accentColor && !logoUrl) return UNBRANDED;
  return {configured: true, accentColor, logoUrl};
}

export function BrandingProvider({children}: {children: ReactNode}) {
  const {status: sessionStatus, client} = useGuestSession();
  const [value, setValue] = useState<BrandingValue>(UNBRANDED);

  useEffect(() => {
    if (sessionStatus !== 'active') return;
    const controller = new AbortController();
    client
      .getBranding(controller.signal)
      .then((branding) => {
        if (controller.signal.aborted) return;
        setValue(toValue(branding));
      })
      // A failed read is "no branding", never a broken menu (foundations §6).
      .catch(() => {
        if (!controller.signal.aborted) setValue(UNBRANDED);
      });
    return () => controller.abort();
  }, [client, sessionStatus]);

  return (
    <BrandingContext.Provider value={value}>
      <AccentStyles accentColor={value.accentColor} />
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding(): BrandingValue {
  return useContext(BrandingContext);
}
